import _ from 'lodash';
import { List } from 'immutable';

import Order from '../records/Order';
import OrderStore from '../stores/OrderStore';
import UserStore from '../stores/UserStore';
import SmartcardStore from '../stores/SmartcardStore';
import LinkingSmartcardsStore from '../stores/LinkingSmartcardsStore';
import InAppStore from '../stores/InAppStore';
import RecentOrdersStore from '../stores/RecentOrdersStore';
import { RECENT_ORDERS_LIMIT } from '../helpers/DuplicateOrder';
import ModalActions from './ModalActions';
import OrderService from '../services/OrderService';
import OrderHistoryService from '../services/OrderHistoryService';
import AppDispatcher from '../dispatcher/AppDispatcher';
import { ActionTypes, CookieKeys, GlobalModals, LoadingTypes } from '../constants/AppConstants';
import StationService from '../services/StationService';
import NotificationActions from './NotificationActions';
import TokenActions from './TokenActions';
import {
    checkOrderExpiry,
    deleteMatchingPassengerAssistRequests,
    handleOrderErrors,
    setOrderDataCookie,
} from '../decorators/Order';
import { updateOrderCookie } from '../utils/orderUtils';
import { createLoadingDecoratorFor } from '../decorators/Loading';
import DisruptionAlertActions from './DisruptionAlertActions';
import RewardPointsActions from './RewardPointsActions';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import PassengerAssistActions from './PassengerAssistActions';
import CookieHelper from '../helpers/Cookie';
import CurrentTripStore from '../stores/CurrentTripStore';
import { downloadBlobFile } from '../utils/fileUtils';


const withOrderLoadingActions = createLoadingDecoratorFor(LoadingTypes.ORDER);

/*
 * Tracks in-flight OrderService.updateFulfilment calls so that actions which invalidate the
 * order (clearBasket, deleteTrip) can wait for them to settle first, instead of letting a
 * delayed fulfilment PUT land on the backend after the order has already been deleted/changed
 * underneath it (CORE-18852)
 */
const pendingFulfilmentUpdates = new Set();

function trackFulfilmentUpdate(promise) {
    const settled = promise.catch(() => {});
    pendingFulfilmentUpdates.add(settled);
    settled.finally(() => pendingFulfilmentUpdates.delete(settled));
    return promise;
}

async function waitForPendingFulfilmentUpdates() {
    await Promise.all(pendingFulfilmentUpdates);
}

const actions = {

    async updateAddress(order) {
        // Guards against stale deferred calls firing after the basket has been cleared, and
        // against syncing fulfilment for an order with no journey to attach it to (CORE-18852)
        if (!order || order.id !== OrderStore.getCurrent().id || order.trips.isEmpty()) { return null; }

        let updatedOrder;
        try {
            updatedOrder = await trackFulfilmentUpdate(OrderService.updateFulfilment(order));
        } catch (error) {
            // The basket can be cleared while this request is in flight - the guard above
            // only catches calls that start after the clear, so re-check here too (CORE-18852)
            if (order.id !== OrderStore.getCurrent().id) { return null; }
            throw error;
        }

        AppDispatcher.dispatch({
            order: updatedOrder,
            actionType: ActionTypes.ORDER_UPDATED
        });

        return updatedOrder;
    },

    @checkOrderExpiry
checkBasket() {
    // returns TRUE only if @checkOrderExpiry allows this action to run
    return true;
},

@withOrderLoadingActions
@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async create({ trip, order }) {
    if (!order) { order = new Order(); }

    this.handleOrderLoading(true);

    AppDispatcher.dispatch({
        trip,
        actionType: ActionTypes.ORDER_STARTED
    });

    try {
        const channel = InAppStore.isInApp() ? InAppStore.getChannel() : 'web';

        const currentTrip = CurrentTripStore.get();

        let updatedTrip = trip;

        /*
            To Do: Remove this when we have a proper way to mutations to the trip in the
            middle of adding to the basket.

            This stems from the fact that we update the bikes count in the current trip
            after the action to create the order has been created with a trip with 0 bikes.
        */
        if (currentTrip.bikes > 0) {
            updatedTrip = updatedTrip.set('bikes', currentTrip.bikes);
        }

        if (currentTrip.hasSeatingPreferences()) {
            updatedTrip = updatedTrip.setSeatingPreferences(currentTrip.reservationPreferences);
        }

        const { order: createdOrder, deviceToken } = await OrderService.create(order, updatedTrip, channel);

        AppDispatcher.dispatch({
            order: createdOrder,
            trip: trip.set('orderTripId', createdOrder.trips.first().id),
            actionType: ActionTypes.ORDER_CREATED
        });

        TokenActions.updateDevice(deviceToken);

        if (FeatureFlagStore.get('enablePassengerAssist')) {
            const mostRecentAddedTrip = createdOrder.trips.last();
            PassengerAssistActions.addOrderTripIdToMostRecentPassengerAssistRequest(mostRecentAddedTrip.id);
        }

        NotificationActions.showNotification('notifications.basket.itemAdded');

        return createdOrder;
    } catch (error) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.ORDER_FAILED,
            error
        });
        throw error;
    } finally {
        this.handleOrderLoading(false);
    }
},

@withOrderLoadingActions
@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async addTrip(order, trip) {
    let updatedOrder = order;

    this.handleOrderLoading(true);

    try {
        const currentTrip = CurrentTripStore.get();

        let updatedTrip = trip;

        /*
            To Do: Remove this when we have a proper way to mutations to the trip in the
            middle of adding to the basket.

            This stems from the fact that we update the bikes count in the current trip
            after the action to create the order has been created with a trip with 0 bikes.
        */
        if (currentTrip.bikes > 0) {
            updatedTrip = updatedTrip.set('bikes', currentTrip.bikes);
        }

        if (currentTrip.hasSeatingPreferences()) {
            updatedTrip = updatedTrip.setSeatingPreferences(currentTrip.reservationPreferences);
        }

        updatedOrder = await OrderService.addTrip(order, updatedTrip);
    } catch (error) {
        if (InAppStore.isInApp()) {
            // InApp crashes when user adds another trip to a non-empty basket.
            // It does not properly clears the basket after logging in/out or when
            // user abandoned the basket that results in invalid X-Customer-Device token.
            // In that case catch the error, clear the basket and create a new order
            // with the currently selected trip.
            this.clearBasket(order);
            updatedOrder = await this.create({ trip });
        } else {
            throw error;
        }
    } finally {
        this.handleOrderLoading(false);
    }

    const knownIds = order.getTripIds();
    const orderTrip = updatedOrder.trips.find((t) => !knownIds.includes(t.getCleanId()));

    AppDispatcher.dispatch({
        orderTrip,
        order: updatedOrder,
        trip: trip.set('orderTripId', orderTrip.id),
        actionType: ActionTypes.ORDER_TRIP_CREATED
    });

    if (FeatureFlagStore.get('enablePassengerAssist')) {
        const mostRecentAddedTrip = updatedOrder.trips.last();
        PassengerAssistActions.addOrderTripIdToMostRecentPassengerAssistRequest(mostRecentAddedTrip.id);
    }

    NotificationActions.showNotification('notifications.basket.itemAdded');

    return updatedOrder;
},

@withOrderLoadingActions
@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
@deleteMatchingPassengerAssistRequests
async deleteTrip(order, orderTrip) {
    // Let any in-flight fulfilment update settle against the still-valid order before
    // removing the trip, instead of racing it (CORE-18852)
    await waitForPendingFulfilmentUpdates();
    await OrderService.deleteTrip(orderTrip);
    const tripDeletedOrder = await OrderService.get(order.id);
    AppDispatcher.dispatch({
        order: tripDeletedOrder,
        orderTrip,
        actionType: ActionTypes.ORDER_TRIP_DELETED
    });

    NotificationActions.showNotification('notifications.basket.itemDeleted');
    return tripDeletedOrder;
},

@withOrderLoadingActions
@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async updateTrip(order, orderTrip, trip) {
    this.handleOrderLoading(true);

    try {
        await OrderService.deleteTrip(orderTrip);
        const tripDeletedOrder = await OrderService.get(order.id);
        const tripAddedOrder = await this.addTrip(tripDeletedOrder, trip);
        return tripAddedOrder;
    } catch (e) {
        throw e;
    } finally {
        this.handleOrderLoading(false);
    }
},

@checkOrderExpiry
async checkTrip({ trip, smartcard }) {
    const order = OrderStore.getCurrent();
    const isSmartcardTrip = trip.isSmartcard();
    const isSmartcardOrder = order.isSmartcard();

    if (order.trips.size && (isSmartcardTrip || isSmartcardOrder)) {
        ModalActions.open(GlobalModals.BASKET);
        return false;
    }

    if (isSmartcardTrip) {
        const user = UserStore.getCurrent();
        if (!user.isLoggedIn()) {
            ModalActions.open(GlobalModals.LOGIN);
            return false;
        }

        if (!smartcard || !smartcard.canLoadProduct()) {
            ModalActions.open(GlobalModals.ERROR, { message: 'booking.error.noSmartcard' });
            return false;
        }
    }

    return { trip };
},

@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
createOrUpdate({ order, trip }) {

    if (!order) { order = OrderStore.getCurrent(); }

    if (!order.id) {
        return this.create({ order, trip });
    }

    return this.addTrip(order, trip);
},

@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async changeTripPassengers(order, trip) {
    if (!order.id) { return false; }
    const orderTrip = order.getTrip(trip.orderTripId);
    if (!orderTrip) { return false; }
    const rrFare = orderTrip.getRoversRangersFare();
    const passengers = trip.passengers.setTicketIds(rrFare.tickets);
    if (!passengers.isComplete()) {
        this.deleteTrip(order, orderTrip);
        return false;
    }

    try {
        if (rrFare.ticketType.isFamilyRanger()) {
            return this.updateTrip(order, orderTrip, trip);
        }

        await OrderService.updatePassengers(order, orderTrip, passengers);
        const updatedOrder = await OrderService.get(order.id);

        AppDispatcher.dispatch({
            order: updatedOrder,
            actionType: ActionTypes.ORDER_TRIP_UPDATED
        });

        return updatedOrder;
    } catch (e) {
        this.deleteTrip(order, orderTrip);
        return false;
    }
},

@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async createOrderForPreRegisteredSmartcard({ trip }) {
    const order = await this.create({ trip });
    const deliveryOptions = await this.loadDeliveryOptions(order);

    const smartcardOption = deliveryOptions.find((option) => option.isSmartcard());

    await this.updateFulfilmentMethod(smartcardOption);
    return this.update();

},

@withOrderLoadingActions
@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async addOrUpdatePlusBus(plusBus, trip, order) {
    if (!order) { order = OrderStore.getCurrent(); }

    const tripWithPlusBus = trip.setPlusBus(plusBus);

    if (!order.id) { return this.create({ order, trip: tripWithPlusBus }); }

    const orderTrip = order.getTrip(trip.orderTripId);

    if (!orderTrip) {
        return this.addTrip(order, tripWithPlusBus);
    }

    if (plusBus !== trip.getPlusBus()) {
        const updatedOrder = await OrderService.updatePlusBus(order, orderTrip, tripWithPlusBus);

        AppDispatcher.dispatch({
            orderTrip: updatedOrder.getTrip(orderTrip.id),
            order: updatedOrder,
            trip: tripWithPlusBus,
            actionType: ActionTypes.ORDER_PLUSBUS_UPDATED
        });
    }

    return order;
},

@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async update({ order } = {}, { disableAddress = false } = {}) {
    const currentOrder = OrderStore.getCurrent();
    if (!order) { order = currentOrder; }
    // Guards against stale deferred calls firing after the basket has been cleared, and
    // against syncing fulfilment for an order with no journey to attach it to (CORE-18852)
    if (!order || order.id !== currentOrder.id || !_.get(order, 'delivery.fulfilmentMethod') || order.trips.isEmpty()) { return null; }

    this.handleOrderLoading(true);

    try {
        const linkedSmartcards = LinkingSmartcardsStore.getAllLinkedSmartcards();
        const updatedOrder = await trackFulfilmentUpdate(OrderService.updateFulfilment(order, { disableAddress, linkedSmartcards }));

        if (FeatureFlagStore.get('enableRewardPoints') && !updatedOrder.isPaid()) {
            RewardPointsActions.registerTicketsForRewardPoints(updatedOrder);
        }

        const user = UserStore.getCurrent();
        const { storedAddress } = order.delivery;
        if (user.isLoggedIn() && order.delivery.isPostalDelivery() && storedAddress) {
            this.updatePostalFulfilmentStoredAddress(storedAddress);
        }

        AppDispatcher.dispatch({
            order: updatedOrder,
            actionType: ActionTypes.ORDER_UPDATED
        });

        return updatedOrder;
    } catch (e) {
        // The basket can be cleared while this request is in flight - the guard above
        // only catches calls that start after the clear, so re-check here too (CORE-18852)
        if (order.id !== OrderStore.getCurrent().id) { return null; }
        throw e;
    } finally {
        this.handleOrderLoading(false);
    }
},

@setOrderDataCookie
clearBasket(order) {
    if (order) {
        /*
         * Fire and forget delete order, so backend can cleanup and release
         * tickets with NRS (CORE-1793)
         */
        // Wait for any in-flight fulfilment update to settle first, instead of racing it (CORE-18852)
        waitForPendingFulfilmentUpdates()
            .then(() => OrderService.delete(order))
            .catch(e => { console.warn(`delete order failed`, e); });
    }

    AppDispatcher.dispatch({
        actionType: ActionTypes.BASKET_CLEARED
    });

    NotificationActions.showNotification('notifications.basket.cleared');

    DisruptionAlertActions.resetTimetableAlerts();
},

@handleOrderErrors
@checkOrderExpiry
handleOrderLoading(value = false) {
    AppDispatcher.dispatch({
        isLoading: value,
        actionType: ActionTypes.ORDER_LOADING
    });
},

@handleOrderErrors
@checkOrderExpiry
handleOrderLoadingDeliveryOptions(value = false) {
    AppDispatcher.dispatch({
        isLoadingDeliveryOptions: value,
        actionType: ActionTypes.ORDER_DELIVERY_OPTIONS_LOADING
    });
},

@handleOrderErrors
@checkOrderExpiry
updateFulfilment(path, value) {
    AppDispatcher.dispatch({
        path,
        value,
        actionType: ActionTypes.FULFILMENT_UPDATED
    });
},

    async deleteSTicketPhoto(userId) {
    try {
        await OrderService.deleteSTicketPhoto(userId);
        return true;
    } catch (err) {
        const errorMessage = _.get(err, 'body.errors.message');
        ModalActions.open(GlobalModals.ERROR, { message: errorMessage });
        return false;
    }
},

    async updateSTicketPhoto(userId, photo) {
    return await OrderService.updateSTicketPhoto(userId, photo);
},

    async sendSTicketPhotoChangeRequest(userId, photo) {
    const request = await OrderService.sendSTicketPhotoChangeRequest(userId, photo);

    NotificationActions.showNotification('sticket.photo.change.notification');

    return request;
},

    async getSTicketPhoto(userId) {
    const photoBlob = await OrderService.getSTicketPhoto(userId);
    return photoBlob;
},

@handleOrderErrors
@checkOrderExpiry
updateFulfilmentMethod(deliveryOption) {
    AppDispatcher.dispatch({
        deliveryOption,
        actionType: ActionTypes.FULFILMENT_METHOD_UPDATED
    });
},

updatePostalFulfilmentStoredAddress(address) {
    AppDispatcher.dispatch({
        address,
        actionType: ActionTypes.POSTAL_FULFILMENT_STORED_ADDRESS_UPDATED
    });
},

@withOrderLoadingActions
async load(orderID) {
    AppDispatcher.dispatch({
        orderID,
        actionType: ActionTypes.ORDER_LOAD_STARTED
    });

    try {
        const order = await OrderService.get(orderID);

        AppDispatcher.dispatch({
            order: order,
            actionType: ActionTypes.ORDER_LOADED
        });
        return order;
    } catch (error) {
        AppDispatcher.dispatch({
            orderID, error,
            actionType: ActionTypes.ORDER_LOAD_FAILED
        });
        throw error;
    }

},

@withOrderLoadingActions
@checkOrderExpiry
@setOrderDataCookie
async refresh(order, { isSettling = false } = {}) {
    AppDispatcher.dispatch({
        order,
        actionType: ActionTypes.ORDER_REFRESH_STARTED
    });

    try {
        const refreshedOrder = await OrderService.get(order.id);

        if (refreshedOrder.isPaid()) {
            AppDispatcher.dispatch({
                order: refreshedOrder,
                appliedVoucher: OrderStore.getAppliedVoucher(),
                actionType: ActionTypes.ORDER_PAID
            });
        } else {
            AppDispatcher.dispatch({
                order: refreshedOrder,
                isSettling,
                actionType: ActionTypes.ORDER_REFRESH_LOADED
            });
        }
        return refreshedOrder;
    } catch (error) {
        AppDispatcher.dispatch({
            order, error,
            isSettling,
            actionType: ActionTypes.ORDER_REFRESH_FAILED
        });
        return null;
    }
},

@setOrderDataCookie
refreshOrderFromCookieData() {
    if (!FeatureFlagStore.get('enableBasketWidget')) {
        return;
    }

    const orderCookie = CookieHelper.get(CookieKeys.ORDER, true);
    const { id: orderId, journeysAmount } = orderCookie && orderCookie.order || {};
    if (!orderId) {
        return;
    }
    if (journeysAmount) {
        this.update();
    } else {
        AppDispatcher.dispatch({ actionType: ActionTypes.BASKET_CLEARED });
    }
},

@withOrderLoadingActions
@checkOrderExpiry
async refreshAmendment(order) {
    AppDispatcher.dispatch({
        order,
        actionType: ActionTypes.ORDER_REFRESH_STARTED
    });

    try {
        const refreshedOrder = await OrderHistoryService.getOrderHistory(order.id);

        if (refreshedOrder.isPaid()) {
            AppDispatcher.dispatch({
                order: refreshedOrder,
                appliedVoucher: OrderStore.getAppliedVoucher(),
                actionType: ActionTypes.ORDER_PAID
            });
        } else {
            AppDispatcher.dispatch({
                order: refreshedOrder,
                actionType: ActionTypes.ORDER_REFRESH_LOADED
            });
        }
        return refreshedOrder;
    } catch (error) {
        AppDispatcher.dispatch({
            order, error,
            actionType: ActionTypes.ORDER_REFRESH_FAILED
        });
        return null;
    }
},

@withOrderLoadingActions
@handleOrderErrors
@checkOrderExpiry
async loadDeliveryOptions(order = OrderStore.getCurrent(), smartcard = SmartcardStore.getCurrent()) {

    if (!order.id) {
        // A persisted order that still has trips but lost its id cannot load
        // delivery options — drop it instead of crashing on every visit.
        if (!order.isEmpty()) {
            updateOrderCookie({});
            AppDispatcher.dispatch({ actionType: ActionTypes.ORDER_EXPIRED });
        }
        return new List();
    }

    this.handleOrderLoadingDeliveryOptions(true);

    try {
        let deliveryOptions = await OrderService.getDeliveryOptions(order.id, smartcard);

        AppDispatcher.dispatch({
            order, deliveryOptions,
            actionType: ActionTypes.ORDER_DELIVERY_OPTIONS_LOADED
        });

        this.handleOrderLoadingDeliveryOptions(false);

        return deliveryOptions;
    } catch (e) {
        this.handleOrderLoadingDeliveryOptions(false);

        throw e;
    }
},

    async checkTaxiBookingAvailability(order) {
    const destinationRequests = order.get('trips')
        .filter((trip) => trip.needsJourney())
        .map((trip) => trip.getDestination())
        .filter((destination) => destination.get('crs'))
        .map((destination) => StationService.search(destination.get('crs')));

    const destinationResponses = await Promise.all(destinationRequests.toArray());

    const onwardTaxiBookingAvailable = destinationResponses
        .some((response) => response.first().get('onwardTaxiBookingAvailable'));

    AppDispatcher.dispatch({
        onwardTaxiBookingAvailable,
        actionType: ActionTypes.ORDER_TAXI_BOOKING_AVAILABILITY
    });

    return onwardTaxiBookingAvailable;
},

    async linkOrderToAccount(token, email) {
    AppDispatcher.dispatch({
        actionType: ActionTypes.LINK_ORDER_TO_ACCOUNT
    });

    return await OrderService.linkToAccount(token, email);
},

updateLeadPassenger(trip, leadPassengerName) {
    AppDispatcher.dispatch({
        trip, leadPassengerName,
        actionType: ActionTypes.ORDER_TRIP_LEAD_PASSENGER_UPDATED
    });
},

@withOrderLoadingActions
@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async applyDiscountCode(discountCode) {
    AppDispatcher.dispatch({
        actionType: ActionTypes.APPLY_DISCOUNT_CODE_STARTED
    });

    try {
        const updatedOrder = await OrderService.applyDiscountCode(OrderStore.getCurrent(), discountCode);

        AppDispatcher.dispatch({
            updatedOrder,
            actionType: ActionTypes.APPLY_DISCOUNT_CODE_FINISHED
        });

        return updatedOrder;
    } catch (error) {
        AppDispatcher.dispatch({
            error,
            actionType: ActionTypes.APPLY_DISCOUNT_CODE_FAILED
        });
        return null;
    }
},

@withOrderLoadingActions
@handleOrderErrors
@checkOrderExpiry
@setOrderDataCookie
async removeDiscountCode(discountCode) {
    AppDispatcher.dispatch({
        actionType: ActionTypes.DELETE_DISCOUNT_CODE_STARTED
    });

    try {
        const updatedOrder = await OrderService.removeDiscountCode(OrderStore.getCurrent(), discountCode);

        AppDispatcher.dispatch({
            updatedOrder,
            actionType: ActionTypes.DELETE_DISCOUNT_CODE_FINISHED
        });

        return updatedOrder;
    } catch (error) {
        AppDispatcher.dispatch({
            error,
            actionType: ActionTypes.DELETE_DISCOUNT_CODE_FAILED
        });
        return null;
    }
},

@withOrderLoadingActions
@checkOrderExpiry
@setOrderDataCookie
async applyVoucherCode(voucherCode) {
    const currentOrder = OrderStore.getCurrent();
    const voucher = await OrderService.applyVoucherCode(currentOrder, voucherCode);
    const updatedOrder = await OrderService.get(currentOrder.id);

    AppDispatcher.dispatch({
        order: updatedOrder,
        voucher,
        actionType: ActionTypes.APPLY_VOUCHER_CODE_FINISHED
    });

    return voucher;
},

@withOrderLoadingActions
@checkOrderExpiry
@setOrderDataCookie
async removeVoucherCode(voucherCode) {
    const currentOrder = OrderStore.getCurrent();
    await OrderService.removeVoucherCode(currentOrder, voucherCode);
    const updatedOrder = await OrderService.get(currentOrder.id);

    AppDispatcher.dispatch({
        order: updatedOrder,
        actionType: ActionTypes.REMOVE_VOUCHER_CODE_FINISHED
    });
},

    async lock(order) {
    try {
        if (!(order && order.id)) {
            console.error('OrderActions.lock error: Invalid order object: ', order);
            return;
        }

        await OrderService.lock(order);
        AppDispatcher.dispatch({
            order,
            actionType: ActionTypes.ORDER_LOCKED
        });
    } catch (error) {
        console.error('OrderActions.lock error: ', error);
    }
},

    async unlock(order, options = {}) {
    if (!(order && order.id)) {
        console.error('OrderActions.unlock error: Invalid order object: ', order);
        return;
    }

    try {
        await OrderService.unlock(order, options);
        AppDispatcher.dispatch({
            order,
            actionType: ActionTypes.ORDER_UNLOCKED,
        });
    } catch (error) {
        console.error('OrderActions.unlock error: ', error);
    }
},

    async loadChangeovers(order, customerId) {
    return OrderService.loadChangeovers(order, customerId);
},

    async updateContactDetails({ email, telephoneNumber }) {
    AppDispatcher.dispatch({
        email,
        telephoneNumber,
        actionType: ActionTypes.ORDER_CONTACT_DETAILS_UPDATED,
    });
},

updateHandoffDetails(handoffDetails) {
    AppDispatcher.dispatch({
        handoffDetails,
        actionType: ActionTypes.ORDER_HANDOFF_UPDATED,
    });
},

updateHandoffReferralReported(referralReported) {
    AppDispatcher.dispatch({
        referralReported,
        actionType: ActionTypes.ORDER_HANDOFF_REFERRAL_REPORTED,
    });
},

validateRewardCode(data) {
    return OrderService.validateRewardCode(data);
},

updateOrder(order) {
    _.defer(() => AppDispatcher.dispatch({
        order: order,
        actionType: ActionTypes.ORDER_UPDATED
    }));
},

    async downloadReceipt(orderId) {
    _.defer(() =>
        AppDispatcher.dispatch({
            actionType: ActionTypes.ORDER_DOWNLOAD_EXPENSE_RECEIPT_STARTED,
        }),
    );
    try {
        const responseBlob = await OrderService.downloadExpenseReceipt(orderId);
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.ORDER_DOWNLOAD_EXPENSE_RECEIPT_SUCCESS,
                responseBlob,
            }),
        );
        downloadBlobFile({ blob: responseBlob, fileName: `Receipt_${orderId}.pdf` });
        return responseBlob;
    } catch (error) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.ORDER_DOWNLOAD_EXPENSE_RECEIPT_FAILED,
                error,
            }),
        );
        ModalActions.open(GlobalModals.ERROR, {
            message: 'account.orders.download-receipt.error',
            customButton: {
                text: 'account.orders.download-receipt.error-modal.retry-btn',
                onClick: () => this.downloadReceipt(orderId),
            }
        });
        return null;
    } finally {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.ORDER_DOWNLOAD_EXPENSE_RECEIPT_FINISHED,
            }),
        );
    }
},

    async downloadMultipleReceipts(startDate, endDate) {
    _.defer(() =>
        AppDispatcher.dispatch({
            actionType: ActionTypes.ORDER_DOWNLOAD_MULTIPLE_RECEIPTS_STARTED,
        }),
    );
    try {
        const user = UserStore.getCurrent();
        const responseBlob = await OrderService.downloadMultipleExpenseReceipts(user.id, startDate, endDate);
        const isEmpty = responseBlob.size === 0;
        if (isEmpty) {
            return null;
        }
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.ORDER_DOWNLOAD_MULTIPLE_RECEIPTS_SUCCESS,
                responseBlob,
            }),
        );
        downloadBlobFile({ blob: responseBlob, fileName: `Receipts_${startDate}_${endDate}.zip` });
        return responseBlob;
    } catch (error) {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.ORDER_DOWNLOAD_MULTIPLE_RECEIPTS_FAILED,
                error,
            }),
        );
        throw error;
    } finally {
        _.defer(() =>
            AppDispatcher.dispatch({
                actionType: ActionTypes.ORDER_DOWNLOAD_MULTIPLE_RECEIPTS_FINISHED,
            }),
        );
    }
},

async loadRecentOrders(user) {
    if (!FeatureFlagStore.get('enableDuplicateOrderCheck') || !user || !user.isLoggedIn()) {
        return;
    }

    try {
        const { orders } = await OrderHistoryService.search(user, { limit: RECENT_ORDERS_LIMIT });
        AppDispatcher.dispatch({
            orders,
            actionType: ActionTypes.RECENT_ORDERS_LOADED,
        });
    } catch (error) {
        console.error('Could not load recent orders for the duplicate order check', error);
    }
},

warnDuplicateOrder(order, onProceed) {
    if (!RecentOrdersStore.requiresDuplicateWarning(order)) {
        return false;
    }

    ModalActions.open(GlobalModals.DUPLICATE_ORDER, {
        isLoggedIn: UserStore.getCurrent().isLoggedIn(),
        orderId: order.id,
        onProceed,
    });

    return true;
},

acknowledgeDuplicateOrder(orderId) {
    AppDispatcher.dispatch({
        orderId,
        actionType: ActionTypes.DUPLICATE_ORDER_ACKNOWLEDGED,
    });
},
};

export default actions;
